import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { DATA_REFRESH_RATE_IN_MILLIS_SHORT } from 'src/utils/constants/constants';
import {
  EventDiscoveryClient,
  EventMetricsTimestamp,
} from 'src/WebApiController';

export const useEventAggregatesMetrics = (
  eventId: number,
  hasAccess?: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  const [timePeriod, setTimePeriod] = useState<EventMetricsTimestamp>(
    EventMetricsTimestamp.LastWeek
  );

  const onUpdateTimePeriod = useCallback(
    (timePeriod: EventMetricsTimestamp) => {
      setTimePeriod(timePeriod ?? EventMetricsTimestamp.LastWeek);
    },
    []
  );

  const shouldQuery = useMemo(
    () =>
      !!activeAccountWebClientConfig.activeAccountId &&
      Boolean(eventId && timePeriod) &&
      Boolean(hasAccess),
    [
      activeAccountWebClientConfig.activeAccountId,
      eventId,
      hasAccess,
      timePeriod,
    ]
  );

  const eventSectionMetricsQuery = useQuery({
    queryKey: [
      'InventoryEventDashboardClient.getEventSectionMetrics',
      eventId,
      timePeriod,
      hasAccess,
    ],
    queryFn: async () => {
      if (!shouldQuery) return null;

      const supplyMetrics = await new EventDiscoveryClient(
        activeAccountWebClientConfig
      ).getEventSectionMetrics(eventId, timePeriod);

      return supplyMetrics;
    },

    enabled: shouldQuery,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: DATA_REFRESH_RATE_IN_MILLIS_SHORT,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('EventAggregatesClient.getEventWebsiteMetrics', error, {
          eventId,
        });
      },
    },
  });

  return {
    sectionMetrics: eventSectionMetricsQuery.data ?? null,
    isLoadingSection: eventSectionMetricsQuery.isLoading,
    timePeriod,
    onUpdateTimePeriod,
  };
};
